<template>
  <div class="pasi-bsa-container d-flex flex-column gap-9">
    <div v-for="(area, areaIndex) in areas" :key="area.key" class="area" :id="`area_${areaIndex}`">
      <h3 class="label text-none">{{ area.name }}</h3>
      <p class="data">
        Área acometida: {{ dataInput[area.key].area }}% e Palmas:
        {{ (dataInput[area.key].area * area.factor).toFixed(2) }}
      </p>
      <v-slider
        v-model="dataInput[area.key].area"
        max="100"
        min="0"
        :class="['mb-4 ml-1', isMobile ? 'max-w-70' : 'max-w-440']"
        hide-details
        dense
      ></v-slider>
      <div v-for="skinProblem in skinProblemTypes" :key="skinProblem.key" class="mb-4">
        <SkinProblemRow
          :label="skinProblem.name"
          v-model="dataInput[area.key][skinProblem.key]"
          :mobile="mobile"
          :mode="showImages ? 'images' : 'numbers'"
        >
          <template v-if="showImages" #levels="level">
            <div class="p-relative">
              <v-img
                :lazy-src="imageFallback"
                :src="
                  level.key === 'ausente'
                    ? `${baseImageUrl}/normal-skin.png`
                    : `${baseImageUrl}/${skinProblem.key}-${level.key}.jpg`
                "
                :max-width="isMobile ? 64 : 88"
                aspect-ratio="1"
                class="rounded-lg click-img skin"
                eager
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <v-icon :size="16" class="expand-icon" @click.stop="(e) => expandImg(level.key, skinProblem.key)">
                mdi-arrow-top-right-bottom-left
              </v-icon>
            </div>
          </template>

          <template v-if="showImages" #label="level">
            <div :class="['caption font-weight-medium font-size-12', isMobile ? 'mobile' : '']">
              <span>{{ level.value }}</span>
              <span v-if="!isMobile">&nbsp;-&nbsp;</span>
              <span>{{ level.label }}</span>
            </div>
          </template>
        </SkinProblemRow>
      </div>
    </div>
    <ImgModal v-model="showImgModal" ref="imgModal" />
  </div>
</template>

<script>
import { PLACEHOLDER_AVATAR_PATH, PUBLIC_STORAGE_URL } from '@/constants/global'
import { isNil, sum } from 'lodash'
import ImgModal from './partial/ImgModal.vue'
import SkinProblemRow from './partial/SkinProblemRow.vue'

export default {
  name: 'PasiBsaCalculator',
  components: { SkinProblemRow, ImgModal },
  props: {
    showImages: {
      type: Boolean,
      default: false,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    fillData: {
      type: Object,
      required: false,
    },
    valid: {
      type: Boolean,
    },
    clearFields: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      showImgModal: false,
      baseImageUrl: PUBLIC_STORAGE_URL,
      imageFallback: PLACEHOLDER_AVATAR_PATH,
      dataInput: this.fillData ?? {
        cabeca_pescoco: {
          eritema: null,
          descamacao: null,
          infiltracao: null,
          area: 0,
        },
        tronco: {
          eritema: null,
          descamacao: null,
          infiltracao: null,
          area: 0,
        },
        membros_superiores: {
          eritema: null,
          descamacao: null,
          infiltracao: null,
          area: 0,
        },
        membros_inferiores: {
          eritema: null,
          descamacao: null,
          infiltracao: null,
          area: 0,
        },
      },
      areas: [
        {
          key: 'cabeca_pescoco',
          name: 'Cabeça e pescoço',
          factor: 0.1,
        },
        {
          key: 'tronco',
          name: 'Tronco',
          factor: 0.3,
        },
        {
          key: 'membros_superiores',
          name: 'Membros superiores	',
          factor: 0.2,
        },
        {
          key: 'membros_inferiores',
          name: 'Membros inferiores',
          factor: 0.4,
        },
      ],
      skinProblemTypes: [
        { key: 'eritema', name: 'Eritema' },
        { key: 'descamacao', name: 'Descamação' },
        { key: 'infiltracao', name: 'Infiltração' },
      ],
    }
  },
  watch: {
    dataInput: {
      deep: true,
      immediate: true,

      handler(newVal) {
        this.$emit('update:valid', this.verifyValid())

        const objArr = [
          {
            key: 'pasi',
            name: 'PASI',
            max: 72,
            min: 0,
            value: this.pasi.toFixed(2),
            sections: [
              {
                limit: 10,
                name: 'Leve',
                color: '#5c9219',
              },
              {
                limit: 72,
                name: 'Moderado a grave',
                color: '#b3261e',
              },
            ],
          },
          {
            key: 'bsa',
            name: 'BSA',
            max: 100,
            min: 0,
            value: this.bsa.toFixed(2),
            sections: [
              {
                limit: 10,
                name: 'Leve',
                color: '#5c9219',
              },
              {
                limit: 100,
                name: 'Moderado a grave',
                color: '#b3261e',
              },
            ],
          },
        ]
        this.$emit('input', objArr)
        this.$emit('calculate', newVal)
      },
    },
    clearFields(newVal) {
      if (newVal) {
        this.dataInput['cabeca_pescoco'].area = 0
        this.dataInput['cabeca_pescoco']['eritema'] = null
        this.dataInput['cabeca_pescoco']['descamacao'] = null
        this.dataInput['cabeca_pescoco']['infiltracao'] = null

        this.dataInput['tronco'].area = 0
        this.dataInput['tronco']['eritema'] = null
        this.dataInput['tronco']['descamacao'] = null
        this.dataInput['tronco']['infiltracao'] = null

        this.dataInput['membros_superiores'].area = 0
        this.dataInput['membros_superiores']['eritema'] = null
        this.dataInput['membros_superiores']['descamacao'] = null
        this.dataInput['membros_superiores']['infiltracao'] = null

        this.dataInput['membros_inferiores'].area = 0
        this.dataInput['membros_inferiores']['eritema'] = null
        this.dataInput['membros_inferiores']['descamacao'] = null
        this.dataInput['membros_inferiores']['infiltracao'] = null

        this.$emit('clear')
      }
    },
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown || this.mobile
    },
    bsa() {
      return sum(
        this.areas.map((area) => {
          const areaData = this.dataInput[area.key]
          return area.factor * areaData.area
        }),
      )
    },
    pasi() {
      return sum(
        this.areas.map((item) => {
          const { area, ...skinProblems } = this.dataInput[item.key]
          const areaWeight = this.getAffectedAreaWeight(area)
          return sum(Object.values(skinProblems)) * item.factor * areaWeight
        }),
      )
    },
  },
  methods: {
    verifyValid() {
      const answeredSectionCount = Object.entries(this.dataInput).filter(
        ([_name, section]) =>
          section.area > 0 && !isNil(section.eritema) && !isNil(section.descamacao) && !isNil(section.infiltracao),
      ).length

      return answeredSectionCount >= 1
    },
    clear() {
      const areas = ['cabeca_pescoco', 'tronco', 'membros_superiores', 'membros_inferiores']
      areas.forEach((key) => {
        this.dataInput[key].eritema = null
        this.dataInput[key].descamacao = null
        this.dataInput[key].infiltracao = null
        this.dataInput[key].area = 0
      })
    },
    expandImg(lvl, skinProblemKey) {
      this.$refs.imgModal.setImgByLevel(lvl, skinProblemKey)
      this.showImgModal = true
    },
    getAffectedAreaWeight(value) {
      if (value === 0) {
        return 0
      }
      if (value < 10) {
        return 1
      }
      if (value >= 10 && value < 30) {
        return 2
      }
      if (value >= 30 && value < 50) {
        return 3
      }
      if (value >= 50 && value < 70) {
        return 4
      }
      if (value >= 70 && value < 90) {
        return 5
      }
      return 6
    },
    getCalculatorData() {
      return {
        pasi_bsa: {
          pasi: this.pasi.toFixed(2),
          bsa: this.bsa.toFixed(2),
          resultado_manual: false,
          ...this.dataInput,
          notes: null,
        },
      }
    },
  },
}
</script>

<style scoped>
.z-10 {
  z-index: 10;
}

:deep(.v-slider__track-background.primary) {
  background-color: #e6e6e6 !important;
  height: 4px;
  border-radius: 4px;
}

:deep(.v-slider__track-fill) {
  height: 4px;
  border-radius: 4px;
}
:deep(.v-slider__thumb) {
  height: 20px;
  width: 20px;
  top: calc(50% + 1px);
}
:deep(.v-slider__thumb::before) {
  height: 40px;
  width: 40px;
  left: -10px;
  top: -10px;
}
:deep(.v-slider__thumb-container--active .v-slider__thumb::before) {
  transform: scale(1.3) !important;
}

:deep(.v-image.skin .v-responsive__content) {
  width: 250px;
}

.click-img:hover {
  cursor: pointer;
}
.click-img::after {
  content: '';
  position: absolute;
  display: block;
  height: 100%;
  width: 100%;
  top: 0;
  opacity: 0;
  box-shadow: inset 0px 0px 0px 2px #006cd0;
  border-radius: 8px;
  transition: all 150ms;
}
.click-img:hover::after {
  opacity: 1;
}

.expand-icon {
  background: #ffffffcc;
  border-radius: 50%;
  padding: 8px;
  margin: 4px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
}
.expand-icon.mobile {
  padding: 4px;
}
.expand-icon:hover {
  background: #ffffff82;
}

.area .label {
  color: var(--v-neutral-lighten17);
  font-size: 22px;
  font-weight: 400;
  margin-bottom: 16px;
}
.area .data {
  color: var(--v-neutral-lighten17);
  font-size: 16px;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.caption {
  display: flex;
  flex-direction: row;
  width: 88px;
  align-items: center;
  justify-content: center;
}

.caption.mobile {
  width: auto;
  flex-direction: column;
}

.max-w-70 {
  max-width: max(70%, 500px);
}

.max-w-440 {
  max-width: 440px;
}

@media screen and (min-width: 2000px) {
  .pasi-bsa-container {
    display: flex;
    flex-direction: row !important;
    flex-wrap: wrap;
  }
  .area {
    margin-right: 96px;
    margin-bottom: 96px;
  }
}
</style>
